import { type PropsWithChildren, useEffect, useRef, useState } from "react";
import { type LinkProps, useNavigate } from "@tanstack/react-router";

type TimedLoaderProps = {
  shouldAutoComplete?: boolean;
  destination?: LinkProps["to"];
};

const MAX_PERCENTAGE_PROGRESS = 90;
const DURATION_IN_SECONDS = 30;
const INTERVAL_IN_MILLISECONDS = 50;

export const TimedLoader = ({
  children,
  destination = "/careplan",
  shouldAutoComplete,
}: PropsWithChildren<TimedLoaderProps>) => {
  const navigate = useNavigate();
  const [percentageProgress, setPercentageProgress] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | number | null>(null);

  useEffect(() => {
    const totalTicks = (DURATION_IN_SECONDS * 1000) / INTERVAL_IN_MILLISECONDS;
    let tick = 0;

    timerRef.current = setInterval(() => {
      tick += 1;
      const newProgress = (tick / totalTicks) * MAX_PERCENTAGE_PROGRESS;
      setPercentageProgress(newProgress);

      if (tick >= totalTicks) {
        clearInterval(timerRef.current!);
        setPercentageProgress(MAX_PERCENTAGE_PROGRESS);
      }
    }, INTERVAL_IN_MILLISECONDS);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (shouldAutoComplete) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      setPercentageProgress(100);

      const navigationTimer = setTimeout(() => {
        navigate({ to: destination });
      }, 500);

      return () => {
        return clearTimeout(navigationTimer);
      };
    }
  }, [shouldAutoComplete, navigate, destination]);

  return (
    <div className="flex min-h-dvh bg-canvas p-5 sm:p-16">
      <div className="mx-auto flex max-w-xl flex-col gap-10 sm:gap-14">
        <div className="h-2 w-full overflow-hidden rounded-full bg-surface-muted">
          <div
            className="h-full bg-surface-inverted transition duration-75 ease-linear"
            style={{ width: `${percentageProgress}%` }}
          />
        </div>
        {children}
      </div>
    </div>
  );
};
